import { useState, type CSSProperties } from 'react';
import { ApiError, apiSendJson } from '../api-client.js';
import { useAuth } from '../auth.js';
import type { DvMode, DvConfigShape } from './InheritanceBadge.js';
import { InheritanceBadge, compareToDefault } from './InheritanceBadge.js';
import { ConfigEditModal } from './ConfigEditModal.js';
import { CascadeOptOutModal } from './CascadeOptOutModal.js';
import { RepoOverrideModal } from './RepoOverrideModal.js';
import { PolicySourceBadge, type PolicySource } from './PolicySourceBadge.js';

export interface OverrideRow {
  repo: string;
  mode: DvMode;
  rerunOnPush: boolean;
  by: string;
  updatedAt: string;
  /** A3 mockup: per-repo policy source. Undefined falls back to the legacy UI. */
  policySource?: PolicySource;
  /** A3 mockup: last DV review timestamp for this repo. */
  lastReviewedAt?: string;
}

interface Props {
  org: string;
  optedIn: boolean;
  defaultConfig: DvConfigShape | null;
  overrides: OverrideRow[];
  onMutated: () => void;
  /** A3 mockup: org-level policy source. Undefined falls back to the legacy UI. */
  policySource?: PolicySource;
  /** A3 mockup: last DV review timestamp for this org (most recent across its repos). */
  lastReviewedAt?: string;
}

type ModalState =
  | { type: 'edit-default' }
  | { type: 'opt-out' }
  | { type: 'add-override' }
  | { type: 'edit-override'; repo: string; current: DvConfigShape }
  | null;

const cardStyle: CSSProperties = {
  background: 'var(--loop-neutral-0)',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-md)',
  marginBottom: 'var(--loop-space-200)',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--loop-space-100)',
  padding: 'var(--loop-space-200) var(--loop-space-250)',
  cursor: 'pointer',
  userSelect: 'none',
};

const bodyStyle: CSSProperties = {
  padding: '0 var(--loop-space-250) var(--loop-space-200)',
  borderTop: '1px solid var(--loop-neutral-200)',
};

const metaTextStyle: CSSProperties = {
  fontSize: '0.8rem',
  color: 'var(--loop-neutral-700)',
};

const chipStyle: CSSProperties = {
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: '999px',
  fontSize: '0.7rem',
  fontWeight: 528,
  background: 'var(--loop-green-100)',
  color: 'var(--loop-green-700)',
  marginLeft: 'auto',
};


const overrideRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 'var(--loop-space-050)',
  padding: 'var(--loop-space-075) 0',
  borderBottom: '1px solid var(--loop-neutral-100)',
};


export function OrgCard({ org, optedIn, defaultConfig, overrides, onMutated, policySource, lastReviewedAt }: Props) {
  const { idToken } = useAuth();

  const defaultExpanded = optedIn && (!!defaultConfig || overrides.length > 0);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [modal, setModal] = useState<ModalState>(null);

  const redundant = overrides.filter((r) => compareToDefault(r, defaultConfig) === 'matches');

  async function handleRemoveOverride(repo: string) {
    if (!window.confirm(`Remove override for ${org}/${repo}?`)) return;
    try {
      await apiSendJson(`/api/dv-config/repo/${encodeURIComponent(org)}/${encodeURIComponent(repo)}`, 'DELETE', idToken!);
      onMutated();
    } catch (e) {
      alert(e instanceof ApiError ? e.body : (e as Error).message);
    }
  }

  async function handleCleanupRedundant() {
    if (!window.confirm(`Remove ${redundant.length} redundant override(s) for ${org}?`)) return;
    try {
      await apiSendJson<unknown>(`/api/dv-config/org/${encodeURIComponent(org)}/cleanup-redundant-overrides`, 'POST', idToken!);
      onMutated();
    } catch (e) {
      alert(e instanceof ApiError ? e.body : (e as Error).message);
    }
  }

  function closeModal() { setModal(null); }
  function savedModal() { setModal(null); onMutated(); }

  return (
    <>
      <div style={cardStyle}>
        <div style={headerStyle} onClick={() => setExpanded((x) => !x)}>
          <span style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-700)', minWidth: 16 }}>
            {expanded ? '▼' : '▶'}
          </span>
          <span style={{ fontWeight: 528, fontSize: '0.9375rem', color: 'var(--loop-neutral-1100)' }}>
            {org}
          </span>
          {!optedIn && defaultConfig && (
            <span style={{ fontSize: '0.8rem', color: 'var(--loop-neutral-600)', marginLeft: 'var(--loop-space-050)' }}>
              (default: {defaultConfig.mode})
            </span>
          )}
          {policySource && (
            <PolicySourceBadge source={policySource} lastReviewedAt={lastReviewedAt} />
          )}
          {optedIn && (
            <span style={chipStyle}>opted in</span>
          )}
        </div>

        {expanded && (
          <div style={bodyStyle}>
            {optedIn ? (
              <>
                <div style={{ paddingTop: 'var(--loop-space-150)', marginBottom: 'var(--loop-space-100)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--loop-space-075)' }}>
                    <span style={{ ...metaTextStyle, fontWeight: 528 }}>Default:</span>
                    <span style={metaTextStyle}>
                      {defaultConfig
                        ? `${defaultConfig.mode} · rerun-on-push: ${defaultConfig.rerunOnPush ? 'on' : 'off'}`
                        : 'not set'}
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--loop-space-100)' }}>
                      <button
                        className="btn-secondary btn-sm"
                        onClick={(e) => { e.stopPropagation(); setModal({ type: 'edit-default' }); }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-secondary btn-sm"
                        style={{ color: 'var(--loop-red-700)' }}
                        onClick={(e) => { e.stopPropagation(); setModal({ type: 'opt-out' }); }}
                      >
                        Opt out
                      </button>
                    </div>
                  </div>
                </div>

                {overrides.length > 0 ? (
                  <>
                    <div style={{ ...metaTextStyle, fontWeight: 528, marginBottom: 'var(--loop-space-075)' }}>
                      Repo overrides ({overrides.length}):
                    </div>
                    {overrides.map((r) => {
                      const cmp = compareToDefault(r, defaultConfig);
                      const isRedundant = cmp === 'matches';
                      return (
                        <div key={r.repo} style={overrideRowStyle}>
                          <span style={{ minWidth: 180, fontFamily: 'var(--loop-font-family-code)', fontSize: '0.8rem', color: 'var(--loop-neutral-1000)' }}>
                            {r.repo}
                          </span>
                          <span style={metaTextStyle}>
                            {r.mode} · rerun:{r.rerunOnPush ? 'on' : 'off'}
                          </span>
                          <InheritanceBadge override={r} orgDefault={defaultConfig} />
                          {r.policySource && (
                            <PolicySourceBadge source={r.policySource} lastReviewedAt={r.lastReviewedAt} compact />
                          )}
                          <span style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--loop-space-100)' }}>
                            {!isRedundant && (
                              <button
                                className="btn-secondary btn-sm"
                                onClick={() => setModal({ type: 'edit-override', repo: r.repo, current: { mode: r.mode, rerunOnPush: r.rerunOnPush } })}
                              >
                                Edit
                              </button>
                            )}
                            <button
                              className="btn-secondary btn-sm"
                              style={{ color: 'var(--loop-red-700)' }}
                              onClick={() => { void handleRemoveOverride(r.repo); }}
                            >
                              {isRedundant ? 'Remove (redundant)' : 'Remove'}
                            </button>
                          </span>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div style={{ ...metaTextStyle, marginBottom: 'var(--loop-space-100)' }}>
                    No repo overrides
                  </div>
                )}

                {redundant.length > 0 && (
                  <div style={{ fontSize: '0.8rem', color: '#d97706', marginTop: 'var(--loop-space-100)', marginBottom: 'var(--loop-space-075)' }}>
                    {redundant.length} redundant override{redundant.length !== 1 ? 's' : ''}.{' '}
                    <button
                      className="btn-secondary btn-sm"
                      style={{ color: '#d97706' }}
                      onClick={() => { void handleCleanupRedundant(); }}
                    >
                      Clean up redundant overrides
                    </button>
                  </div>
                )}

                <div style={{ marginTop: 'var(--loop-space-100)' }}>
                  <button
                    className="btn-primary"
                    onClick={() => setModal({ type: 'add-override' })}
                  >
                    + Add repo override
                  </button>
                </div>
              </>
            ) : (
              <div style={{ paddingTop: 'var(--loop-space-150)', display: 'flex', alignItems: 'center', gap: 'var(--loop-space-150)' }}>
                <span style={metaTextStyle}>Diff Vader not enabled for this org.</span>
              </div>
            )}
          </div>
        )}
      </div>

      {modal?.type === 'edit-default' && (
        <ConfigEditModal
          title={`Edit ${org} default`}
          initial={defaultConfig ?? { mode: 'shadow', rerunOnPush: false }}
          endpoint={`/api/dv-config/org/${encodeURIComponent(org)}`}
          onClose={closeModal}
          onSaved={savedModal}
          org={org}
        />
      )}

      {modal?.type === 'opt-out' && (
        <CascadeOptOutModal
          org={org}
          overrideCount={overrides.length}
          hasDefault={defaultConfig !== null}
          onClose={closeModal}
          onSaved={savedModal}
        />
      )}

      {modal?.type === 'add-override' && (
        <RepoOverrideModal
          org={org}
          initial={{ mode: defaultConfig?.mode ?? 'shadow', rerunOnPush: defaultConfig?.rerunOnPush ?? false }}
          onClose={closeModal}
          onSaved={savedModal}
        />
      )}

      {modal?.type === 'edit-override' && (
        <RepoOverrideModal
          org={org}
          existingRepo={modal.repo}
          initial={modal.current}
          onClose={closeModal}
          onSaved={savedModal}
        />
      )}
    </>
  );
}
